import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { usePortalLoad, type Failure, type PortalLoad } from './offline';

/** Who the office user is looking at, when this is "View as"; null for the contractor's own session. */
export interface Viewing {
  name: string;
  by: string;
}
export const ViewingContext = createContext<Viewing | null>(null);
export const useViewing = () => useContext(ViewingContext);

/**
 * The controls that change something. While viewing as a contractor they are switched off
 * here, section by section, so links, Back and "Try again" keep working. The server refuses
 * every write while viewing anyway; this only makes the screen say so.
 */
export function Actions(props: { children: ReactNode }) {
  const viewing = useViewing();
  if (!viewing) return <>{props.children}</>;
  return (
    <fieldset className="p-actions" disabled>
      {props.children}
    </fieldset>
  );
}

const WIDE = '(min-width: 900px)';
const media = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(WIDE)
    : null;

/** True on a screen wide enough to plan on: the list and the visit side by side. */
export function useWide(): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = media();
      m?.addEventListener('change', l);
      return () => m?.removeEventListener('change', l);
    },
    () => media()?.matches ?? false,
  );
}

/** One tap to ring the office, if the business has set a number. */
export function OfficeCall(props: { className?: string }) {
  const { data } = usePortalLoad<{ office_phone?: string }>('/api/portal/me');
  const phone = data?.office_phone?.trim();
  if (!phone) return null;
  return (
    <a
      className={`button p-call ${props.className ?? ''}`}
      href={`tel:${phone.replace(/[^\d+]/g, '')}`}
    >
      Call the office<span className="sr-only"> on {phone}</span>
    </a>
  );
}

/** "6:10 pm" today, or "Tue 29 Sep, 6:10 pm" on another day. */
export function savedWhen(iso: string, now = new Date()): string {
  const at = new Date(iso);
  if (!iso || Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (at.toDateString() === now.toDateString()) return time;
  const day = at.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${day}, ${time}`;
}

/**
 * The line over a saved copy: why it is not fresh, when it is from, and a way to ask again.
 * `extra` says what that means on this screen ("It may have been taken since").
 */
export function SavedNote(props: { load: PortalLoad<unknown>; extra?: string }) {
  const { saved, savedAt, why, retry, loading } = props.load;
  // Still asking: said only after a second, so a good signal never flashes a note.
  const checking = saved && why === '';
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!checking) return;
    const t = setTimeout(() => setSlow(true), 1000);
    return () => clearTimeout(t);
  }, [checking]);
  if (!saved) return null;
  const when = savedWhen(savedAt);
  const from = when ? `what this phone saved at ${when}` : 'what was saved on this phone';
  if (checking) {
    return slow ? (
      <div className="p-saved">
        <p>Checking for changes. Showing {from}.</p>
      </div>
    ) : null;
  }
  return (
    <div className="p-saved" role="status">
      <p>
        {why === 'server' ? (
          <>
            <strong>Couldn’t refresh.</strong> The problem is on our side, not your signal. Showing{' '}
            {from}.
          </>
        ) : (
          <>
            <strong>No signal.</strong> Showing {from}.
          </>
        )}
        {props.extra && <> {props.extra}</>}
      </p>
      <button type="button" className="quiet" onClick={retry} disabled={loading}>
        {loading ? 'Trying…' : 'Try again'}
      </button>
    </div>
  );
}

function explain(f: Failure, notFound?: string): string {
  if (f.offline)
    return 'There is no signal, and nothing is saved on this phone yet. It will load by itself when you are back online.';
  if (f.status >= 500) {
    return 'The problem is on our side, not your phone or your signal. Try again in a moment. If it keeps happening, call the office and quote the reference.';
  }
  if (f.status === 404 && notFound) return notFound;
  return f.message;
}

/**
 * A load that did not work, in place of the section that could not load. The page keeps
 * its heading. Says what failed and whose problem it is, and always offers a way forward:
 * try again, call the office, and a reference the office can look up.
 */
export function LoadFailure(props: {
  what: string;
  load: PortalLoad<unknown>;
  notFound?: string;
  children?: ReactNode;
}) {
  const f = props.load.failure;
  const viewing = useViewing();
  const [copied, setCopied] = useState(false);
  if (!f) return null;
  const details = [
    `GET ${f.url}`,
    f.status ? `answered ${f.status}` : 'got no answer',
    f.requestId && `reference ${f.requestId}`,
    viewing && `viewing as ${viewing.name}`,
  ]
    .filter(Boolean)
    .join(', ');

  async function copy() {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
    } catch {
      // No clipboard: the details are on screen to read out.
    }
  }

  return (
    <div className="p-fail" role="alert">
      <h2>Couldn’t load {props.what}</h2>
      <p>{explain(f, props.notFound)}</p>
      <div className="p-fail-actions">
        <button
          type="button"
          className="p-big"
          onClick={props.load.retry}
          disabled={props.load.loading}
        >
          {props.load.loading ? 'Trying…' : 'Try again'}
        </button>
        {!viewing && <OfficeCall className="p-big" />}
      </div>
      {props.children}
      {f.requestId && !viewing && (
        <p className="p-ref">
          Reference <span className="p-code">{f.requestId}</span>
        </p>
      )}
      {viewing && (
        <div className="p-diag">
          <p>
            <strong>For the office:</strong> <span className="p-code">{details}</span>.{' '}
            {f.status >= 500 && `${viewing.name.split(' ')[0]} is probably seeing this too.`}
          </p>
          <button type="button" className="quiet" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy details'}
          </button>
        </div>
      )}
    </div>
  );
}
